import React, { useState, useCallback, useEffect } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import "./equipment.scss";
import { useTerminalsList, useEquipmentList, useEquipmentTypes } from "hooks/useEquipment";
import ContentContainer from "templates/content-container/contentContainer";
import { PAGE_IDS } from "app/data/common/pageIds";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import EquipmentViewDetails from "./equipmentView/equipmentView";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import Tag from "ui-components/molecules/tag/tag";
import EquipmentAddEditDetails from "./equipmentView/equipmentAddEditDetails";
import { TagColor } from "app/data/common/tagColor";
import { statusOptions } from "app/data/equipment/models";
import { Formik } from 'formik';
import { Popover } from "ui-components/popover/popover";
import LabeledSelectInput from "ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";

interface Option {
  label: string;
  value: string;
}

const columnHelper = createColumnHelper<any>();

const Equipment: React.FC<{}> = () => {
  const { data: equipmentTypeOptions = [], isLoading: isTypeLoading } = useEquipmentTypes();
  const { data: terminalOptions = [] } = useTerminalsList();
  const [filterType, setFilterType] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<string[]>([]);
  const [filterTerminal, setFilterTerminal] = useState<string[]>([]);

  const [filterTypeOptions, setFilterTypeOptions] = useState<Option[]>([]);
  const [filterStatusOptions, setFilterStatusOptions] = useState<Option[]>([]);
  const [filterTerminalOptions, setFilterTerminalOptions] = useState<Option[]>([]);

  const [equipmentSearch, setEquipmentSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [equipmentNumber, setEquipmentNumber] = useState<string | null>(null);
  const [showCreateTeam, setShowCreateTeam] = useState<boolean>(false);
  const [editingEquipmentNumber, setEditingEquipmentNumber] = useState<string | null>(null);
  const tableDisabled = showCreateTeam || !!editingEquipmentNumber;

  useEffect(() => {
    setFilterType(filterTypeOptions.map(o => o.value));
    setFilterStatus(filterStatusOptions.map(o => o.value));
    setFilterTerminal(filterTerminalOptions.map(o => o.value));
  }, [filterTypeOptions, filterStatusOptions, filterTerminalOptions]);

  const columns = [
    columnHelper.accessor("unitNumber", {
      id: "unitNumber",
      header: () => (
        <>
          Unit Number
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Unique ID assigned to each equipment unit.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 60,
    }),
    columnHelper.accessor("equipmentType", {
      id: "equipmentType",
      header: () => (
        <>
          Type
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Category of equipment.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 40,
    }),
    columnHelper.accessor("assetDescriptor", {
      id: "assetDescriptor",
      header: () => (
        <>
          Asset Descriptor
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Describes the asset configuration, like Sleeper, Straight Truck, or TADC (Tandem Axle).
                </div>
              }
            />
          </span>
        </>
      ),
      size: 70,
    }),

    columnHelper.accessor("make", {
      id: "make",
      header: "Make",
      size: 30,
    }),

    columnHelper.accessor("model", {
      id: "model",
      header: "Model",
      size: 5,
    }),

    columnHelper.accessor("year", {
      id: "year",
      header: () => (
        <>
          Year
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Model year of the equipment.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 0,
    }),

    columnHelper.accessor("dotInspectionDate", {
      id: "dotInspectionDate",
      header: () => (
        <>
          DOT Inspection Date
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Next scheduled Department of Transportation inspection date.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 75,
    }),

    columnHelper.accessor("location", {
      id: "location",
      header: () => (
        <>
          Terminal
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Terminal or hub where this equipment is currently assigned.
                </div>
              }
            />
          </span>
        </>
      ),
      size: 5,
    }),

    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <>
          Status
          <span onClick={(e) => e.stopPropagation()}>
            <Popover
              values={
                <div className="popover-value">
                  Shows whether the equipment is Active, Under Maintenance, or Out of Service.
                </div>
              }
            />
          </span>
        </>
      ),
      cell: (cellProps) => {
        let cellValue = cellProps.getValue()?.toUpperCase();
        return (
          <Tag mods={{
            color: cellValue === "ACTIVE" ? TagColor.GREEN : TagColor.GREY, uppercase: true
          }}>
            {cellValue}
          </Tag >
        )
      },
      size: 0,
    })
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error
  } = useEquipmentList(
    equipmentSearch || undefined,
    filterType.length > 0 ? filterType.join(',') : undefined,
    filterStatus.length > 0 ? filterStatus.join(',') : undefined,
    filterTerminal.length > 0 ? filterTerminal.join(',') : undefined
  );


  useEffect(() => {
    const timer = setTimeout(() => {
      setEquipmentSearch(searchInput.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleScroll = useCallback(() => {
    const scrollContainer = document.querySelector(".xgs-equipment-master__orders");
    if (!scrollContainer) return;

    const bottom =
      scrollContainer.scrollHeight - scrollContainer.scrollTop <=
      scrollContainer.clientHeight + 10;

    if (bottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);


  const onSearchStart = useCallback(() => {
    setEquipmentSearch(searchInput.trim());
  }, [searchInput]);

  const onSearchClear = (resetForm: (nextValues?: any) => void) => () => {
    setSearchInput("");
    setFilterTypeOptions([]);
    setFilterStatusOptions([]);
    setFilterTerminalOptions([]);
    setFilterType([]);
    setFilterStatus([]);
    setFilterTerminal([]);
    setEquipmentSearch("");
    resetForm({ values: { type: [], status: [], terminal: [] } });
  };


  return (
    <ContentContainer
      pageId={PAGE_IDS.EQUIPMENT}
      title="Equipment Master"
      tooltip="This module is in Preview. Certain data and features may be incomplete or under development."
      status="PREVIEW"
    >
      <div className="xgs-equipment-master">
        <div className="xgs-list__controls">
          <Formik
            initialValues={{ type: filterType, status: filterStatus, terminal: filterTerminal }}
            enableReinitialize={true}
            onSubmit={onSearchStart}
          >
            {({ getFieldProps, resetForm }) => (
              <div className="xgs-list__controls__search">
                <LabeledTextInput
                  label="Search Equipment"
                  labelMode={LabelModes.column}
                  className="xgs-list__controls__search__input"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onKeyDown={(e) => (e.key === "Enter") && !isLoading && setEquipmentSearch(searchInput.trim())}
                  type="text"
                  disabled={tableDisabled}
                  placeholder="Search by Unit, VIN, Make, Model..."
                />

                <LabeledSelectInput
                  label="Equipment Type"
                  labelMode={LabelModes.column}
                  isMulti
                  isClearable
                  isSearchable
                  isLoading={isTypeLoading}
                  onMultiValuesChange={(options: Option[] | null) => {
                    setFilterTypeOptions(options ?? []);
                  }}
                  value={filterTypeOptions}
                  options={equipmentTypeOptions as Option[]}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Types"
                />

                <LabeledSelectInput
                  label="Equipment Status"
                  labelMode={LabelModes.column}
                  isMulti
                  isClearable
                  isSearchable={false}
                  onMultiValuesChange={(options: Option[] | null) => {
                    setFilterStatusOptions(options ?? []);
                  }}
                  value={filterStatusOptions}
                  disabled={tableDisabled}
                  options={statusOptions as Option[]}
                  className="xgs-item-form__field filter-select"
                  placeholder="All Status"
                />

                <LabeledSelectInput
                  label="Terminal Location"
                  labelMode={LabelModes.column}
                  isMulti
                  isClearable
                  isSearchable
                  isLoading={isTypeLoading}
                  onMultiValuesChange={(options: Option[] | null) => {
                    setFilterTerminalOptions(options ?? []);
                  }}
                  value={filterTerminalOptions}
                  options={terminalOptions as Option[]}
                  className="xgs-item-form__field filter-select"
                  disabled={tableDisabled}
                  placeholder="All Terminals"
                />

                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.blue}
                  disabled={isLoading || tableDisabled}
                  onClick={onSearchStart}>
                  Search
                </Button>

                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.gray}
                  disabled={tableDisabled}
                  onClick={onSearchClear(resetForm)}>
                  Clear Filters
                </Button>
              </div>
            )}
          </Formik>
          <div className="xgs-list__controls__actions">
            <Button
              className="xgs-equipment-master__create-equipment-button"
              theme={ButtonThemes.blue}
              onClick={() => setShowCreateTeam(true)}
            >
              + Add Equipment
            </Button>
          </div>
        </div>
        {error && (
          <XGSErrorMessage>{error}</XGSErrorMessage>
        )}
        {!error && (
          <div className="xgs-equipment-master__orders" style={{ position: 'relative' }}>
            <Table
              isLoading={isLoading}
              columns={columns}
              data={data?.allItems || []}
              noResultsText="There are no records available"
              infiniteScroll
              infiniteScrollLoading={isFetchingNextPage}
              infiniteScrollHasNext={!!hasNextPage}
              onInfiniteScroll={handleScroll}
              responsive={true}
              sorting={true}
              minTableHeight={420}
              cursorPointer={!tableDisabled}
              onRowClicked={(row: any) => {
                if (!tableDisabled) {
                  setEquipmentNumber(row.id);
                }
              }}
              autoResetSelectedRows={true}
            />
          </div>
        )}
        <EquipmentAddEditDetails
          show={showCreateTeam}
          onClose={() => setShowCreateTeam(false)}
          onTeamCreated={() => setEquipmentSearch("")}
        />
        <EquipmentViewDetails
          equipmentNumber={equipmentNumber}
          show={!!equipmentNumber}
          onClose={() => { setEquipmentNumber(null); setShowCreateTeam(false); }}
          onEdit={(id: string) => setEditingEquipmentNumber(id)}
          onEditClose={() => { setEditingEquipmentNumber(null); setEquipmentNumber(null); }}
        />
      </div>
    </ContentContainer>
  );
};

export default Equipment;
